import ConfirmModal from "../components/ConfirmModal";
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../utils/api";

const USDT_RATE = 95;
const fmtTs  = (ts) => { if (!ts) return "—"; return new Date(ts * 1000).toLocaleString(); };
const toUSDT = (inr) => (parseFloat(inr) / USDT_RATE).toFixed(4);
const fmtBal = (bal, cur) => {
  const n = parseFloat(bal) || 0;
  if (cur === "USDT") return `$${toUSDT(n)} USDT`;
  return `₹${n.toFixed(2)}`;
};
const fmtPrice = (p, cur) => {
  const n = parseFloat(p) || 0;
  if (cur === "USDT") return `$${toUSDT(n)} USDT`;
  return `₹${n.toFixed(2)}`;
};

export default function Admin({ currentUser }) {
  const [confirmDelUser, setConfirmDelUser] = useState(null);
  const [confirmClearHist, setConfirmClearHist] = useState(false);
  const [tab,     setTab]     = useState("users");
  const [users,   setUsers]   = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [histTarget,  setHistTarget]  = useState(null);
  const [userHist,    setUserHist]    = useState([]);
  const [fundTarget,  setFundTarget]  = useState(null);
  const [fundAmt,     setFundAmt]     = useState("");
  const [fundNote,    setFundNote]    = useState("");
  const [fundErr,     setFundErr]     = useState("");
  const [fundSaving,  setFundSaving]  = useState(false);
  const [fundMode,    setFundMode]    = useState("add"); // "add" or "minus"
  const [fundHist,    setFundHist]    = useState([]);
  const [fundHistTarget, setFundHistTarget] = useState(null);

  const emptyForm = {
    username: "", password: "", role: "user", notes: "",
    phone: "", price_per_member: "2.00", reaction_price_per_member: "0.00", display_currency: "INR",
    reactions_enabled: true,
  };
  const [form,    setForm]    = useState(emptyForm);
  const [formErr, setFormErr] = useState("");
  const [saving,  setSaving]  = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try { setUsers(await api.adminGetUsers()); } catch {}
    setLoading(false);
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try { setHistory(await api.adminGetHistory(300)); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

  const handleCreate = async () => {
    if (!form.username.trim()) { setFormErr("Username required"); return; }
    if (!form.password)        { setFormErr("Password required"); return; }
    if (!form.phone.trim())    { setFormErr("Phone number required"); return; }
    const price = parseFloat(form.price_per_member);
    if (isNaN(price) || price < 0) { setFormErr("Invalid price"); return; }
    setSaving(true); setFormErr("");
    try {
      await api.adminCreateUser({ ...form, price_per_member: price });
      setCreateModal(false); setForm(emptyForm); await loadUsers();
    } catch (e) { setFormErr(e.message); }
    setSaving(false);
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setForm({
      username: u.username, password: "", role: u.role, notes: u.notes || "",
      phone: u.phone || "",
      price_per_member: parseFloat(u.price_per_member || 2).toFixed(2),
      reaction_price_per_member: parseFloat(u.reaction_price_per_member || 0).toFixed(2),
      display_currency: u.display_currency || "INR",
      reactions_enabled: !!u.reactions_enabled,
    });
    setFormErr("");
  };

  const handleEdit = async () => {
    const price = parseFloat(form.price_per_member);
    if (isNaN(price) || price < 0) { setFormErr("Invalid price"); return; }
    const body = {
      role: form.role, notes: form.notes, phone: form.phone,
      price_per_member: price, reaction_price_per_member: parseFloat(form.reaction_price_per_member) || 0,
      display_currency: form.display_currency,
      reactions_enabled: form.reactions_enabled,
    };
    if (form.password) body.password = form.password;
    setSaving(true); setFormErr("");
    try { await api.adminUpdateUser(editTarget.id, body); setEditTarget(null); setForm(emptyForm); await loadUsers(); }
    catch (e) { setFormErr(e.message); }
    setSaving(false);
  };

  const openAddFund   = (u) => { setFundTarget(u); setFundAmt(""); setFundNote(""); setFundErr(""); setFundMode("add"); };
  const openMinusFund = (u) => { setFundTarget(u); setFundAmt(""); setFundNote(""); setFundErr(""); setFundMode("minus"); };

  const handleAddFund = async () => {
    const isMinus = fundMode === "minus";
    const isUSDT = fundTarget?.display_currency === "USDT";
    const amt = parseFloat(fundAmt);
    if (isNaN(amt) || amt <= 0) { setFundErr("Enter a valid amount"); return; }
    setFundSaving(true); setFundErr("");
    try {
      const amtToSend = isUSDT ? amt * USDT_RATE : amt;
      if (isMinus) {
        await api.adminDeductFund(fundTarget.id, { amount: amtToSend, note: fundNote });
      } else {
        await api.adminAddFund(fundTarget.id, { amount: amtToSend, note: fundNote });
      }
      setFundTarget(null); await loadUsers();
    } catch (e) { setFundErr(e.message); }
    setFundSaving(false);
  };

  const openFundHist = async (u) => {
    setFundHistTarget(u);
    try { setFundHist(await api.adminGetFundHistory(u.id)); } catch { setFundHist([]); }
  };

  const toggleActive = async (u) => { try { await api.adminUpdateUser(u.id, { is_active: !u.is_active }); await loadUsers(); } catch (e) { /* error silenced */ } };
  const handleDelete = async (u) => { setConfirmDelUser(u); };
  const doDeleteUser = async (u) => { setConfirmDelUser(null); try { await api.adminDeleteUser(u.id); await loadUsers(); } catch (e) { } };
  const openUserHistory = async (u) => { setHistTarget(u); const h = await api.adminGetUserHistory(u.id); setUserHist(h); };
  const clearHistory = async () => { setConfirmClearHist(true); };
  const doClearHistory = async () => { setConfirmClearHist(false); await api.adminClearHistory(); setHistory([]); };

  return (
    <div style={{ animation: "floatUp .35s ease" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#1c1917", letterSpacing: "-.4px" }}>Admin Panel</div>
          <div style={{ fontSize: 12, color: "#78716c", fontWeight: 600, marginTop: 2 }}>User management &amp; login history</div>
        </div>
        {tab === "users" && (
          <button className="adm-btn adm-btn-primary" onClick={() => { setCreateModal(true); setForm(emptyForm); setFormErr(""); }}>
            <PlusIcon /> New User
          </button>
        )}
        {tab === "history" && (
          <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={clearHistory}><TrashIcon /> Clear History</button>
        )}
      </div>

      <div className="adm-tabs">
        <button className={`adm-tab${tab === "users" ? " active" : ""}`} onClick={() => setTab("users")}>
          <UsersIcon /> Users <span className="adm-badge adm-badge-blue" style={{ marginLeft: 6, fontSize: 10 }}>{users.length}</span>
        </button>
        <button className={`adm-tab${tab === "history" ? " active" : ""}`} onClick={() => setTab("history")}>
          <ClockIcon /> Login History
        </button>
      </div>

      {tab === "users" && (
        <div className="adm-card">
          {loading && <div className="adm-empty">Loading…</div>}
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr>
                <th>Username</th><th>Role</th><th>Status</th><th>Phone</th>
                <th>Price/Member</th><th>Balance</th><th>Reactions</th>
                <th>Last Login</th><th>Notes</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {users.map(u => {
                  const cur = u.display_currency || "INR";
                  const bal = parseFloat(u.balance) || 0;
                  const balColor = bal < 0 ? "#dc2626" : bal < 50 ? "#d97706" : "#15803d";
                  return (
                    <tr key={u.id}>
                      <td>
                        <span style={{ fontWeight: 700, color: "#292524" }}>{u.username}</span>
                        {u.username === currentUser && <span style={{ marginLeft: 6, fontSize: 10, color: "#b45309" }}>(you)</span>}
                      </td>
                      <td>
                        <span className={`adm-badge ${u.role === "admin" ? "adm-badge-blue" : "adm-badge-gray"}`}>
                          {u.role === "admin" ? "👑 Admin" : "👤 User"}
                        </span>
                      </td>
                      <td>
                        <span className={`adm-badge ${u.is_active ? "adm-badge-green" : "adm-badge-gray"}`}>
                          {u.is_active ? "Active" : "Disabled"}
                        </span>
                      </td>
                      <td><span className="adm-mono" style={{ fontSize: 12 }}>{u.phone || "—"}</span></td>
                      <td>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#9333ea" }}>
                          {fmtPrice(u.price_per_member, cur)}
                        </span>
                        <span style={{ marginLeft: 5, fontSize: 10, color: "#78716c", background: "#e7e5e4", borderRadius: 4, padding: "1px 5px" }}>{cur}</span>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: balColor }}>
                            {fmtBal(bal, cur)}
                          </span>
                          {u.role !== "admin" && <button
                            className="adm-btn adm-btn-sm"
                            style={{ fontSize: 10, padding: "2px 7px", color: "#15803d", border: "1px solid #15803d" }}
                            onClick={() => openAddFund(u)}
                            title="Add Fund"
                          >+ Fund</button>}
                          {u.role !== "admin" && <button
                            className="adm-btn adm-btn-sm"
                            style={{ fontSize: 10, padding: "2px 7px", color: "#dc2626", border: "1px solid #dc2626" }}
                            onClick={() => openMinusFund(u)}
                            title="Minus Fund"
                          >− Fund</button>}
                          <button
                            className="adm-btn adm-btn-ghost adm-btn-sm"
                            style={{ fontSize: 10, padding: "2px 6px" }}
                            onClick={() => openFundHist(u)}
                            title="Fund History"
                          ><ClockIcon /></button>
                        </div>
                      </td>
                      <td>
                        <span className={`adm-badge ${u.reactions_enabled ? "adm-badge-green" : "adm-badge-gray"}`}>
                          {u.reactions_enabled ? "On" : "Off"}
                        </span>
                      </td>
                      <td><span className="adm-mono" style={{ fontSize: 11 }}>{fmtTs(u.last_login) || "Never"}</span></td>
                      <td style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{ fontSize: 12, color: "#78716c" }}>{u.notes || "—"}</span>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "nowrap" }}>
                          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => openUserHistory(u)} title="Login History"><ClockIcon /></button>
                          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => openEdit(u)} title="Edit"><EditIcon /></button>
                          <button
                            className="adm-btn adm-btn-sm"
                            style={{ fontSize: 11, color: u.is_active ? "#d97706" : "#15803d", border: `1px solid ${u.is_active ? "#d97706" : "#15803d"}`, padding: "3px 8px" }}
                            onClick={() => toggleActive(u)}
                          >
                            {u.is_active ? "Disable" : "Enable"}
                          </button>
                          {u.username !== currentUser && (
                            <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => handleDelete(u)} title="Delete"><TrashIcon /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && users.length === 0 && (
                  <tr><td colSpan="10" style={{ textAlign: "center", color: "#d6d3d1", padding: 28 }}>No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="adm-card">
          {loading && <div className="adm-empty">Loading…</div>}
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr><th>Time</th><th>Username</th><th>IP Address</th><th>Result</th><th>Reason</th></tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td><span className="adm-mono">{fmtTs(h.ts)}</span></td>
                    <td style={{ fontWeight: 600, color: "#292524" }}>{h.username}</td>
                    <td><span className="adm-mono">{h.ip}</span></td>
                    <td><span className={`adm-badge ${h.success ? "adm-badge-green" : "adm-badge-red"}`}>{h.success ? "✓ Success" : "✗ Failed"}</span></td>
                    <td style={{ fontSize: 12, color: "#292524", fontWeight: 600 }}>{h.reason || "—"}</td>
                  </tr>
                ))}
                {!loading && history.length === 0 && (
                  <tr><td colSpan="5" style={{ textAlign: "center", color: "#d6d3d1", padding: 28 }}>No login history</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Create New User">
        <UserForm form={form} setForm={setForm} showUsername />
        {formErr && <div className="adm-form-err">{formErr}</div>}
        <div className="adm-modal-footer">
          <button className="adm-btn" onClick={() => setCreateModal(false)}>Cancel</button>
          <button className="adm-btn adm-btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Creating…" : "Create User"}</button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title={`Edit User — ${editTarget?.username}`}>
        <UserForm form={form} setForm={setForm} showUsername={false} />
        {formErr && <div className="adm-form-err">{formErr}</div>}
        <div className="adm-modal-footer">
          <button className="adm-btn" onClick={() => setEditTarget(null)}>Cancel</button>
          <button className="adm-btn adm-btn-primary" onClick={handleEdit} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </Modal>

      {/* Add Fund Modal */}
      <Modal open={!!fundTarget} onClose={() => setFundTarget(null)} title={`${fundMode === "minus" ? "Minus Fund" : "Add Fund"} — ${fundTarget?.username}`}>
        <div style={{ padding: "0 0 8px" }}>
          <div style={{ fontSize: 12, color: "#78716c", marginBottom: 14 }}>
            Current balance: <strong style={{ color: "#15803d" }}>{fundTarget ? fmtBal(fundTarget.balance, fundTarget.display_currency || "INR") : ""}</strong>
            &nbsp;·&nbsp; Currency: <strong style={{ color: "#9333ea" }}>{fundTarget?.display_currency || "INR"}</strong>
          </div>
          <div className="adm-form-row">
            <label className="adm-label">Amount ({fundTarget?.display_currency || "INR"})</label>
            <input
              className="adm-input" type="number" min="0.01" step="0.01"
              placeholder={fundTarget?.display_currency === "USDT" ? "e.g. 10.00 USDT" : "e.g. 500.00 ₹"}
              value={fundAmt} onChange={e => setFundAmt(e.target.value)}
            />
            {fundTarget?.display_currency === "USDT" && parseFloat(fundAmt) > 0 && (
              <div style={{ marginTop: 5, fontSize: 11, color: "#78716c" }}>
                = ₹{(parseFloat(fundAmt) * USDT_RATE).toFixed(2)} INR stored internally
              </div>
            )}
            {fundTarget?.display_currency === "INR" && parseFloat(fundAmt) > 0 && (
              <div style={{ marginTop: 5, fontSize: 11, color: "#78716c" }}>
                ≈ ${(parseFloat(fundAmt) / USDT_RATE).toFixed(4)} USDT
              </div>
            )}
          </div>
          <div className="adm-form-row">
            <label className="adm-label">Note (optional)</label>
            <input className="adm-input" placeholder="e.g. Monthly recharge" value={fundNote} onChange={e => setFundNote(e.target.value)} />
          </div>
          {fundErr && <div className="adm-form-err">{fundErr}</div>}
        </div>
        <div className="adm-modal-footer">
          <button className="adm-btn" onClick={() => setFundTarget(null)}>Cancel</button>
          <button
            className="adm-btn adm-btn-primary"
            style={fundMode === "minus" ? { background: "#ef4444", borderColor: "#ef4444" } : {}}
            onClick={handleAddFund}
            disabled={fundSaving}
          >
            {fundSaving ? (fundMode === "minus" ? "Deducting…" : "Adding…") : (fundMode === "minus" ? "Deduct Fund" : "Add Fund")}
          </button>
        </div>
      </Modal>

      {/* Fund History Modal */}
      <Modal open={!!fundHistTarget} onClose={() => setFundHistTarget(null)} title={`Fund History — ${fundHistTarget?.username}`} width={620}>
        <div className="adm-table-wrap" style={{ maxHeight: 360, overflowY: "auto" }}>
          <table className="adm-table">
            <thead><tr><th>Time</th><th>Type</th><th>Amount</th><th>Note</th><th>By</th></tr></thead>
            <tbody>
              {fundHist.map(h => (
                <tr key={h.id}>
                  <td><span className="adm-mono" style={{ fontSize: 11 }}>{fmtTs(h.created_at)}</span></td>
                  <td>
                    <span className={`adm-badge ${h.type === "credit" ? "adm-badge-green" : "adm-badge-red"}`}>
                      {h.type === "credit" ? "▲ Credit" : "▼ Debit"}
                    </span>
                  </td>
                  <td style={{ fontWeight: 700, color: h.type === "credit" ? "#15803d" : "#dc2626" }}>
                    {h.type === "credit" ? "+" : "-"}{fmtBal(h.amount, fundHistTarget?.display_currency || "INR")}
                  </td>
                  <td style={{ fontSize: 12, color: "#78716c" }}>{h.note || "—"}</td>
                  <td><span className="adm-mono" style={{ fontSize: 11 }}>{h.created_by}</span></td>
                </tr>
              ))}
              {fundHist.length === 0 && (
                <tr><td colSpan="5" style={{ textAlign: "center", color: "#d6d3d1", padding: 20 }}>No transactions yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="adm-modal-footer">
          <button className="adm-btn" onClick={() => setFundHistTarget(null)}>Close</button>
        </div>
      </Modal>

      {/* Login History Modal */}
      <Modal open={!!histTarget} onClose={() => setHistTarget(null)} title={`Login History — ${histTarget?.username}`} width={640}>
        <div className="adm-table-wrap" style={{ maxHeight: 380, overflowY: "auto" }}>
          <table className="adm-table">
            <thead><tr><th>Time</th><th>IP</th><th>Result</th><th>Reason</th></tr></thead>
            <tbody>
              {userHist.map(h => (
                <tr key={h.id}>
                  <td><span className="adm-mono">{fmtTs(h.ts)}</span></td>
                  <td><span className="adm-mono">{h.ip}</span></td>
                  <td><span className={`adm-badge ${h.success ? "adm-badge-green" : "adm-badge-red"}`}>{h.success ? "✓ Success" : "✗ Failed"}</span></td>
                  <td style={{ fontSize: 12, color: "#292524", fontWeight: 600 }}>{h.reason || "—"}</td>
                </tr>
              ))}
              {userHist.length === 0 && (
                <tr><td colSpan="4" style={{ textAlign: "center", color: "#d6d3d1", padding: 20 }}>No history</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="adm-modal-footer">
          <button className="adm-btn" onClick={() => setHistTarget(null)}>Close</button>
        </div>
      </Modal>
      <ConfirmModal
        open={!!confirmDelUser}
        title="Delete User?"
        message={<>Delete user <strong style={{color:"#dc2626"}}>{confirmDelUser?.username}</strong>? This cannot be undone.</>}
        confirmLabel="Delete"
        onConfirm={() => doDeleteUser(confirmDelUser)}
        onCancel={() => setConfirmDelUser(null)}
      />
      <ConfirmModal
        open={confirmClearHist}
        title="Clear All Login History?"
        message="This will permanently delete ALL login history records. This cannot be undone."
        confirmLabel="Clear All"
        onConfirm={doClearHistory}
        onCancel={() => setConfirmClearHist(false)}
      />
  </div>
  );
}

function Modal({ open, onClose, title, children, width = 520 }) {
  return (
    <div className={`adm-modal-bg${open ? " open" : ""}`} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="adm-modal" style={{ width }}>
        <div className="adm-modal-head">
          <div className="adm-modal-title">{title}</div>
          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={onClose}><XIcon /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      display: "inline-flex", alignItems: "center", cursor: "pointer",
      width: 44, height: 24, borderRadius: 12,
      background: value ? "#22c55e" : "#d6d3d1",
      transition: "background .2s", padding: "0 3px",
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: "50%", background: "#fff",
        transform: value ? "translateX(20px)" : "translateX(0)",
        transition: "transform .2s",
      }} />
    </div>
  );
}

function UserForm({ form, setForm, showUsername }) {
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const price = parseFloat(form.price_per_member) || 0;

  return (
    <>
      {showUsername && (
        <div className="adm-form-row">
          <label className="adm-label">Username</label>
          <input className="adm-input" placeholder="e.g. john_doe" value={form.username} onChange={f("username")} />
        </div>
      )}
      <div className="adm-form-row">
        <label className="adm-label">Phone Number *</label>
        <input className="adm-input" placeholder="e.g. +91 98765 43210" value={form.phone} onChange={f("phone")} />
      </div>
      <div className="adm-form-row">
        <label className="adm-label">{showUsername ? "Password" : "New Password (leave blank to keep)"}</label>
        <input type="password" className="adm-input" placeholder={showUsername ? "Min 4 characters" : "Leave blank to keep"} value={form.password} onChange={f("password")} />
      </div>
      <div className="adm-form-row">
        <label className="adm-label">Role</label>
        <select className="adm-input" value={form.role} onChange={f("role")}>
          <option value="user">👤 User — standard access</option>
          <option value="admin">👑 Admin — full access + user management</option>
        </select>
      </div>
      <div className="adm-form-row">
        <label className="adm-label">Display Currency</label>
        <div style={{ display: "flex", gap: 8 }}>
          {["INR", "USDT"].map(c => (
            <button key={c} type="button"
              onClick={() => setForm(p => ({ ...p, display_currency: c }))}
              style={{
                flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13,
                background: form.display_currency === c ? (c === "INR" ? "#7c3aed" : "#f59e0b") : "#e7e5e4",
                color: form.display_currency === c ? "#fff" : "#78716c",
                transition: "all .15s",
              }}
            >{c === "INR" ? "₹ INR" : "$ USDT"}</button>
          ))}
        </div>
      </div>
      <div className="adm-form-row">
        <label className="adm-label">Price per Member ({form.display_currency}) — default ₹2.00</label>
        <input className="adm-input" type="number" min="0" step="0.01" placeholder="2.00" value={form.price_per_member} onChange={f("price_per_member")} />
        {price > 0 && form.display_currency === "INR" && (
          <div style={{ marginTop: 5, fontSize: 11, color: "#78716c" }}>≈ ${(price / USDT_RATE).toFixed(4)} USDT &nbsp;·&nbsp; Rate: 1 USDT = ₹{USDT_RATE}</div>
        )}
        {price > 0 && form.display_currency === "USDT" && (
          <div style={{ marginTop: 5, fontSize: 11, color: "#78716c" }}>= ₹{(price * USDT_RATE).toFixed(2)} INR stored internally</div>
        )}
      </div>
      <div className="adm-form-row">
        <label className="adm-label">Reaction Price per Member ({form.display_currency}) — charged when reaction is enabled</label>
        <input className="adm-input" type="number" min="0" step="0.01" placeholder="0.00" value={form.reaction_price_per_member} onChange={f("reaction_price_per_member")} />
        <div style={{ marginTop: 5, fontSize: 11, color: "#78716c" }}>If 0, normal price applies even with reactions</div>
      </div>
      <div className="adm-form-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <label className="adm-label" style={{ marginBottom: 0 }}>Enable Reactions</label>
        <Toggle value={form.reactions_enabled} onChange={v => setForm(p => ({ ...p, reactions_enabled: v }))} />
      </div>
      <div className="adm-form-row">
        <label className="adm-label">Notes (optional)</label>
        <input className="adm-input" placeholder="e.g. Marketing team" value={form.notes} onChange={f("notes")} />
      </div>
    </>
  );
}

function PlusIcon()  { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function XIcon()     { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function EditIcon()  { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>; }
function ClockIcon() { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }
function UsersIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>; }
